import React, { FC, useEffect } from "react";
import {
	Field,
	FieldRenderProps,
	FieldWrapper,
} from "@progress/kendo-react-form";
import {
	FormDropDownList,
	FormDatePicker,
	FormInput,
	FormDropDownListWithSearch,
	ItemLabel,
} from "ui/Topography";
import { useState } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import {
	getCatchAreaApi,
	getErsApi,
	getFishingGearApi,
	getQuotaTypeApi,
	getTerminalsApi,
} from "api/common";
import {
	getCountriesApi,
	getFoodSafetyApprovalNumberApi,
	getSearchUsageApi,
} from "api/reportSearch";
import {
	DROPDOWN_ITEMS,
	getLandingTypeField,
	getYesOrNoField,
} from "utils/dropdown/items";
import { DATE_FORMAT } from "utils/constant";
import {
	CatchArea,
	Country,
	DropDownItem,
	ErsMessage,
	FishingGear,
} from "interface/common";
import { useInfoStore } from "store/error";
import { LandingNoteHead } from "interface/landingNote";
import { saveLandingNoteHead } from "api/landingNote";
import {
	requirementFields,
	handleRequirementCheck,
	areAllRequirementsMet,
} from "../utils";
import { getExemptionVesselApi } from "api/vessel";
import { ExemptionVessel } from "interface/vessel";
import { ExemptionVesselRenderItem } from "ui/dropdownItemList/VesselRenderItem";
import { useTranslation } from "react-i18next";
import { useToastStore } from "store/toast";

const FormStepOne: FC<{
	form: LandingNoteHead;
	setForm: (form: LandingNoteHead) => void;
	setAllRequirementsMet: (allMet: boolean) => void;
	showRequirementMetError: boolean;
	setFormSaved: (saved: boolean) => void;
}> = ({
	form,
	setForm,
	setAllRequirementsMet,
	showRequirementMetError,
	setFormSaved,
}) => {
	const { t } = useTranslation();
	const [quotaType, setQuotaType] = useState<DropDownItem[]>([]);
	const [countries, setCountries] = useState<Country[]>([]);
	const [quotaVessel, setQuotaVessel] = useState<ExemptionVessel[]>([]);
	const [erMessages, setErMessages] = useState<ErsMessage[]>([]);

	const [foodSafetyApproveNumber, setFoodSafetyApproveNumber] = useState<
		DropDownItem[]
	>([]);
	const [requirementMet, setRequirementMet] = useState(requirementFields);
	const { setError } = useInfoStore();
	const { showWarning } = useToastStore();

	useEffect(() => {
		setRequirementMet(handleRequirementCheck(form, erMessages));
		setAllRequirementsMet(
			areAllRequirementsMet(handleRequirementCheck(form, erMessages)),
		);
	}, [form, setAllRequirementsMet]);

	useEffect(() => {
		getErs();
		getQuotaVessel();
	}, [form]);

	const getQuotaTypeData = () => {
		getSearchUsageApi()
			.then((data) => {})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
		getQuotaTypeApi()
			.then((data) => {
				setQuotaType(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const getCountries = () => {
		if (countries.length > 0) {
			return;
		}
		getCountriesApi()
			.then((data) => {
				setCountries(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const getFoodSafetyApprovalNumber = () => {
		if (!form.buyerFdirId) {
			return;
		}
		getFoodSafetyApprovalNumberApi(form.buyerFdirId)
			.then((data) => {
				setFoodSafetyApproveNumber(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const saveForm = (form: LandingNoteHead) => {
		setForm(form);
		form.noteStatus = 2;
		setFormSaved(false);
		saveLandingNoteHead(form)
			.then((response) => {
				setFormSaved(true);
				// console.log(response);
				//showSuccess(`${t("Saved")} ${t("Success")}`, { duration: 2000 });
			})
			.catch((err) => {
				console.log(err);
				//silent failed
				// if (err?.response?.data) {
				// 	setError(err?.response?.data.returnText);
				// } else {
				// 	setError(err.message);
				// }
			});
	};

	const getQuotaVessel = () => {
		if (!form.vesselHullId || quotaVessel.length > 0) {
			return;
		}
		const lastCatchDate = form.lastCatchDate
			? new Date(form.lastCatchDate)
			: undefined;
		getExemptionVesselApi(form.vesselHullId, lastCatchDate as any)
			.then((data) => {
				setQuotaVessel(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};
	const getErs = () => {
		if (erMessages.length > 0 || !form?.vesselRadioCallSignal) {
			return;
		}

		getErsApi({
			landingTime: form.landingTime || (new Date() as any),
			radioCallSign: form.vesselRadioCallSignal as any,
		})
			.then((data) => {
				setErMessages(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const ersData = erMessages.find(
		(msg) => msg.recordNumber === form.recordNumberErs,
	);
	const allowQuotaNationEditCode = "04"; // "ANNET LANDS KVOTE";
	const showVesselQuota = quotaVessel.length > 0;

	return (
		<>
			<div
				className="landing-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr 1fr",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				{/*<Field*/}
				{/*	id={"Fangstdagbok"}*/}
				{/*	name={"Fangstdagbok"}*/}
				{/*	label={t("CatchDiary") + "*"}*/}
				{/*	// placeholder={"example"}*/}
				{/*	currentValue={form?.diaryNumber}*/}
				{/*	type={"number"}*/}
				{/*	component={FormInput}*/}
				{/*	isValid={handleIsValid(*/}
				{/*		showRequirementMetError,*/}
				{/*		requirementMet.diaryNumber,*/}
				{/*	)}*/}
				{/*	onChange={(e: any) => {*/}
				{/*		saveForm({ ...form, diaryNumber: e.value });*/}
				{/*	}}*/}
				{/*/>*/}
				{/*<Field*/}
				{/*	id={"Tur_nummer*"}*/}
				{/*	name={"Tur nummer*"}*/}
				{/*	label={t("TripNumber") + "*"}*/}
				{/*	// placeholder={"emt"}*/}
				{/*	component={FormInput}*/}
				{/*	currentValue={form?.tripNumber}*/}
				{/*	isValid={handleIsValid(*/}
				{/*		showRequirementMetError,*/}
				{/*		requirementMet.tripNumber,*/}
				{/*	)}*/}
				{/*	type={"number"}*/}
				{/*	onChange={(e: any) => {*/}
				{/*		saveForm({ ...form, tripNumber: e.value || null });*/}
				{/*	}}*/}
				{/*/>*/}

				<Field
					id={"ERS-meldingsnr"}
					name={"ERS-meldingsnr"}
					label={`${
						t("ErsMessageNumber") + (erMessages.length > 0 ? "*" : "")
					}`}
					textField={"displayName"}
					dataItemKey={"recordNumber"}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.recordNumberErs,
						)
					}
					// currentValue={data.buyer}
					data={erMessages}
					currentValue={{
						recordNumber: ersData?.recordNumber,
						displayName: ersData?.displayName,
					}}
					loadDataOnClicked={getErs}
					onSelect={(data: ErsMessage) => {
						let _data: any = {
							recordNumberErs: data.recordNumber || undefined,
							ersId: data.ersId,
						};
						if (!form.vesselCaptainsName) {
							_data = {
								..._data,
								vesselCaptainsName: data.vesselMaster || undefined,
							};
						}
						saveForm({
							...form,
							..._data,
						});
					}}
					component={FormDropDownList}
				/>
				<Field
					id={"FørsteFangstdato*"}
					name={"FørsteFangstdato"}
					label={t("FirstCatchDate") + "*"}
					currentValue={form.firstCatchDate}
					placeholder=""
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.firstCatchDate,
					)}
					min={form.departureDate ? new Date(form.departureDate) : undefined}
					format={DATE_FORMAT}
					onChange={(e: any) => {
						if (
							form.lastCatchDate &&
							new Date(e.value) > new Date(form.lastCatchDate)
						) {
							showWarning(t("FirstCatchDateLessThanLastCatchDate"), {
								duration: 6000,
								pauseOnHover: true,
								position: "bottom-center",
								style: {
									paddingTop: "40px",
									paddingBottom: "40px",
								},
							});
							return;
						}
						saveForm({ ...form, firstCatchDate: formatDate(e.value) });
					}}
					component={FormDatePicker}
				/>

				<Field
					id={"Siste_Fangstdato*"}
					name={"Siste_Fangstdato*"}
					label={t("LastCatchDate") + "*"}
					currentValue={form.lastCatchDate}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.lastCatchDate,
					)}
					min={form.firstCatchDate ? new Date(form.firstCatchDate) : undefined}
					onChange={(e: any) => {
						if (
							form.firstCatchDate &&
							new Date(e.value).setHours(0, 0, 0, 0) <
								new Date(form.firstCatchDate).setHours(0, 0, 0, 0)
						) {
							showWarning(t("LastCatchDateGreaterThanFirstCatchDate"), {
								duration: 6000,
								pauseOnHover: true,
								position: "bottom-center",
								style: {
									paddingTop: "40px",
									paddingBottom: "40px",
								},
							});
							return;
						}
						saveForm({
							...form,
							lastCatchDate: formatDate(e.value),
							catchYear: e.value.getFullYear(),
						});
					}}
					placeholder=""
					format={DATE_FORMAT}
					component={FormDatePicker}
				/>

				<Field
					id={"Fangst"}
					name={"Fangstår"}
					label={t("CatchYear") + "*"}
					placeholder={""}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.catchYear,
					)}
					currentValue={form.catchYear}
					component={FormInput}
					type={"number"}
					onChange={(e: any) => {
						saveForm({ ...form, catchYear: e.value });
					}}
				/>
			</div>

			<div
				className="landing-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr 1fr",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				<Field
					id={"Mannskap"}
					name={"Mannskap"}
					label={t("CrewSize") + "*"}
					placeholder={""}
					component={FormInput}
					currentValue={form.crewSize}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.crewSize,
					)}
					type={"number"}
					onChange={(e: any) =>
						saveForm({ ...form, crewSize: e.value || null })
					}
				/>
				<Field
					id={"Herav_norsk_mannskap*"}
					name={"Herav_norsk_mannskap*"}
					label={t("CrewSizeNorwegian") + "*"}
					placeholder={""}
					component={FormInput}
					currentValue={form.norwegianCrewSize}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.norwegianCrewSize,
					)}
					type={"number"}
					onChange={(e: any) =>
						saveForm({ ...form, norwegianCrewSize: e.value || null })
					}
				/>

				<Field
					id={"Norsk_skipper*"}
					name={"Norsk_skipper*"}
					label={t("VesselOfficersNorwegian") + "*"}
					data={DROPDOWN_ITEMS.yesOrNo}
					textField={"field"}
					dataItemKey={"value"}
					// defaultItem={{ field: "Choose", value: "" }}
					currentValue={{
						value: form?.vesselOfficersNorwegian,
						field: getYesOrNoField(form?.vesselOfficersNorwegian as any).field,
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.vesselOfficersNorwegian,
						)
					}
					onSelect={(data: { field: string; value: number | string }) => {
						let value: number | undefined | string = data.value;
						if (data.value === "") {
							value = undefined;
						}
						saveForm({ ...form, vesselOfficersNorwegian: value as any });
					}}
					component={FormDropDownList}
				/>
				<Field
					id={"Skipper"}
					name={"Skipper"}
					label={t("Skipper") + "*"}
					placeholder={""}
					component={FormInput}
					currentValue={form.vesselCaptainsName}
					onChange={(e: any) => {
						setForm({ ...form, vesselCaptainsName: e.value });
						//saveForm({ ...form, vesselCaptainsName: e.value })
					}}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.vesselCaptainsName,
					)}
					saveItem={(e: any) => {
						saveForm(form);
					}}
				/>
				{/*<Field*/}
				{/*	id={"Avgangsdatodato"}*/}
				{/*	name={"Avgangsdatodato"}*/}
				{/*	label={t("DepartureDate") + "*"}*/}
				{/*	currentValue={form.departureDate}*/}
				{/*	placeholder={""}*/}
				{/*	format={DATE_FORMAT}*/}
				{/*	min={form.firstCatchDate ? new Date(form.firstCatchDate) : undefined}*/}
				{/*	// isValid={handleIsValid(*/}
				{/*	// 	showRequirementMetError,*/}
				{/*	// 	requirementMet.departureDate*/}
				{/*	// )}*/}
				{/*	onChange={(e) => {*/}
				{/*		if (*/}
				{/*			form.arrivalDate &&*/}
				{/*			new Date(e.value) > new Date(form.arrivalDate as any)*/}
				{/*		) {*/}
				{/*			showWarning(t("DepartureDateLessThanArrivalDate"), {*/}
				{/*				duration: 6000,*/}
				{/*				pauseOnHover: true,*/}
				{/*				position: "bottom-center",*/}
				{/*				style: {*/}
				{/*					paddingTop: "40px",*/}
				{/*					paddingBottom: "40px",*/}
				{/*				},*/}
				{/*			});*/}
				{/*			return;*/}
				{/*		}*/}
				{/*		saveForm({ ...form, departureDate: formatDate(e.value) });*/}
				{/*	}}*/}
				{/*	component={FormDatePicker}*/}
				{/*/>*/}

				{/*<Field*/}
				{/*	id={"Ankomstdato*"}*/}
				{/*	name={"Ankomstdato"}*/}
				{/*	label={t("ArrivalDate") + "*"}*/}
				{/*	currentValue={form.arrivalDate}*/}
				{/*	value={form.arrivalDate}*/}
				{/*	// isValid={handleIsValid(*/}
				{/*	// 	showRequirementMetError,*/}
				{/*	// 	requirementMet.arrivalDate*/}
				{/*	// )}*/}
				{/*	placeholder={""}*/}
				{/*	format={DATE_FORMAT}*/}
				{/*	min={form.lastCatchDate ? new Date(form.lastCatchDate) : undefined}*/}
				{/*	onChange={(e) => {*/}
				{/*		if (*/}
				{/*			form.departureDate &&*/}
				{/*			new Date(e.value) < new Date(form.departureDate as any)*/}
				{/*		) {*/}
				{/*			showWarning(t("ArrivalDateGreaterThanDepartureDate"), {*/}
				{/*				duration: 6000,*/}
				{/*				pauseOnHover: true,*/}
				{/*				position: "bottom-center",*/}
				{/*				style: {*/}
				{/*					paddingTop: "40px",*/}
				{/*					paddingBottom: "40px",*/}
				{/*				},*/}
				{/*			});*/}
				{/*			return;*/}
				{/*		}*/}
				{/*		saveForm({ ...form, arrivalDate: formatDate(e.value) });*/}
				{/*	}}*/}
				{/*	component={FormDatePicker}*/}
				{/*/>*/}

			{/*	<Field*/}
			{/*		id={"Fangstdøgn"}*/}
			{/*		name={"Fangstdøgn"}*/}
			{/*		label={t("DaysFished")}*/}
			{/*		placeholder={""}*/}
			{/*		currentValue={form.daysFished}*/}
			{/*		component={FormInput}*/}
			{/*		type={"number"}*/}
			{/*		onChange={(e) => {*/}
			{/*			saveForm({ ...form, daysFished: e.value });*/}
			{/*		}}*/}
			{/*	/>*/}
			</div>

			<div
				className="landing-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: showVesselQuota ? "1fr 1fr  1fr " : "1fr 1fr",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				{showVesselQuota && (
					<Field
						id={"quotaVessel"}
						name={"quotaVessel"}
						label={t("QuotaVessel")}
						value={t("Choose") + " " + t("QuotaVessel")}
						textField={"quotaVesselName"}
						dataItemKey={"id"}
						disabled={form.lastCatchDate ? false : true}
						loadDataOnClicked={getQuotaVessel}
						data={quotaVessel}
						defaultItem={{
							quotaVesselName: t("Choose") + " " + t("QuotaVessel"),
							id: null,
						}}
						currentValue={{
							id: form.quotaVesselHullId,
							quotaVesselName: form.quotaVesselName,
						}}
						onSelect={(quotaVessel: ExemptionVessel) => {
							saveForm({
								...form,
								quotaVesselHullId: quotaVessel.quotaVesselHullId as any,
								quotaVesselName: quotaVessel.quotaVesselName,
								quotaVesselRadioCallSignal:
									quotaVessel.quotaVesselRadioCallSignal,
								quotaVesselTypeId: quotaVessel.quotaVesselTypeId as any,
								quotaVesselRegistrationId:
									quotaVessel.quotaVesselRegistrationId as any,
							});
						}}
						itemRender={ExemptionVesselRenderItem}
						component={FormDropDownList}
					/>
				)}

				<Field
					id={"Redskap"}
					name={"Redskap"}
					label={t("FishingGear") + "*"}
					value={t("ChooseFishingGear")}
					popupSettings={{
						// width: "100%",
						className: "landing-note-mobile-dropdown-popup",
					}}
					filterable={true}
					textField={"displayName"}
					dataItemKey={"id"}
					filterApi={getFishingGearApi}
					defaultItem={{
						displayName: t("ChooseFishingGear"),
						id: null,
					}}
					currentValue={{
						id: form.fishingGearId,
						displayName: form.fishingGear,
					}}
					onSelect={(fishingGear: FishingGear) => {
						saveForm({
							...form,
							fishingGearId: fishingGear.id,
							fishingGear: fishingGear.name,
						});
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.fishingGearId,
						)
					}
					component={FormDropDownListWithSearch}
				/>
				<Field
					name={"Fangstfelt"}
					//label={"Fangstfelt"}
					label={t("CatchArea") + "*"}
					popupSettings={{
						// width: "100%",
						className: "landing-note-mobile-dropdown-popup",
					}}
					filterable={true}
					textField={"displayName"}
					dataItemKey={"id"}
					filterApi={getCatchAreaApi}
					defaultItem={{
						displayName: t("ChooseCatchArea"),
						id: null,
					}}
					currentValue={{
						id: form.catchField,
						displayName: form.catchField
							? `${form.catchField} |  ${form.catchAreaZone} | ${form.catchAreaQuotaZone} | ${form.catchArea}`
							: "",
					}}
					showErrorMessage={
						!handleIsValid(showRequirementMetError, requirementMet.catchField)
					}
					// className="error"
					onSelect={(catchArea: CatchArea) => {
						saveForm({
							...form,
							catchField: catchArea.code,
							catchAreaZone: catchArea.zone,
							catchAreaQuotaZone: catchArea.groundFDZone,
							catchArea: catchArea.name,
						});
					}}
					component={FormDropDownListWithSearch}
				/>
			</div>

			<div
				className="landing-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr 1fr ",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				<Field
					id={"Kvotetype*"}
					name={"Kvotetype*"}
					label={t("QuotaType") + "*"}
					textField={"name"}
					dataItemKey={"code"}
					data={quotaType}
					currentValue={{ code: form.quotaTypeCode, name: form.quotaType }}
					loadDataOnClicked={getQuotaTypeData}
					onChange={(e: { value: { code: string; name: string } }) => {
						const data: any = {
							quotaTypeCode: e.value.code,
							quotaType: e.value.name,
						};
						if (allowQuotaNationEditCode === e.value.code) {
						}
						saveForm({
							...form,
							quotaTypeCode: e.value.code,
							quotaType: e.value.name,
						});
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.quotaTypeCode,
						)
					}
					component={FormDropDownList}
				/>

				<Field
					id={"Kvotenasjon"}
					name={"Kvotenasjon"}
					label={t("QuotaNation") + "*"}
					textField={"englishDescription"}
					dataItemKey={"code"}
					placeholder={""}
					currentValue={{
						code: form.quotaNationCode,
						englishDescription: form.quotaNation,
					}}
					loadDataOnClicked={getCountries}
					data={countries}
					disabled={form?.quotaTypeCode !== allowQuotaNationEditCode}
					onChange={(e: { value: Country }) => {
						saveForm({
							...form,
							quotaNationCode: e.value.code,
							quotaNation: e.value.description,
						});
					}}
					component={FormDropDownList}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.quotaNationCode,
						)
					}
				/>

				<Field
					id={"Landingsnasjon"}
					name={"Landingsnasjon"}
					label={t("LandingNation") + "*"}
					textField={"englishDescription"}
					dataItemKey={"code"}
					loadDataOnClicked={getCountries}
					data={countries}
					currentValue={{
						code: form.landingNationCode,
						englishDescription: form.landingNation,
					}}
					onChange={(e: { value: Country }) => {
						saveForm({
							...form,
							landingNationCode: e.value.code,
							landingNation: e.value.description,
						});
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.landingNationCode,
						)
					}
					component={FormDropDownList}
				/>
				<Field
					id={"Landingstype"}
					name={"Landingstype"}
					label={t("LandingType") + "*"}
					data={DROPDOWN_ITEMS.landingType}
					textField={"field"}
					dataItemKey={"value"}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.partialLandingId,
						)
					}
					currentValue={getLandingTypeField(form.partialLandingId as any)}
					component={FormDropDownList}
					onChange={(e: { value: { field: string; value: number } }) => {
						saveForm({ ...form, partialLandingId: e.value.value });
					}}
				/>
			</div>

			<div
				className="landing-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr 1fr ",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				<Field
					id={"Godkjenningsnummer"}
					name={"Godkjenningsnummer"}
					label={t("Godkjenningsnummer") + "*"}
					placeholder={""}
					component={FormInput}
					currentValue={form.vesselApprovalNumber}
					onChange={(e: any) => {
						setForm({ ...form, vesselApprovalNumber: e.value });
						//saveForm({ ...form, vesselCaptainsName: e.value })
					}}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.vesselApprovalNumber,
					)}
					saveItem={(e: any) => {
						saveForm(form);
					}}
				/>
				{/*<Field*/}
				{/*	id={"Forrige sted"}*/}
				{/*	name={"Forrige sted"}*/}
				{/*	label={t("PreviousTerminal")}*/}
				{/*	filterable={true}*/}
				{/*	textField={"displayName"}*/}
				{/*	dataItemKey={"code"}*/}
				{/*	filterApi={getTerminalsApi}*/}
				{/*	defaultItem={{*/}
				{/*		displayName: "Choose",*/}
				{/*		code: null,*/}
				{/*	}}*/}
				{/*	currentValue={{*/}
				{/*		code: form.previousReceivingFacility,*/}
				{/*		displayName: form.previousReceivingFacilityText,*/}
				{/*	}}*/}
				{/*	// className="error"*/}
				{/*	onSelect={(data: DropDownItem) => {*/}
				{/*		saveForm({*/}
				{/*			...form,*/}
				{/*			previousReceivingFacility: data.code,*/}
				{/*			previousReceivingFacilityText: data.displayName,*/}
				{/*		});*/}
				{/*	}}*/}
				{/*	component={FormDropDownListWithSearch}*/}
				{/*/>*/}
			{/*	<Field*/}
			{/*		id={"Neste sted"}*/}
			{/*		name={"Neste sted"}*/}
			{/*		label={t("NextTerminal")}*/}
			{/*		filterable={true}*/}
			{/*		textField={"displayName"}*/}
			{/*		dataItemKey={"code"}*/}
			{/*		filterApi={getTerminalsApi}*/}
			{/*		defaultItem={{*/}
			{/*			displayName: "Choose",*/}
			{/*			code: null,*/}
			{/*		}}*/}
			{/*		currentValue={{*/}
			{/*			code: form.nextReceivingFacility,*/}
			{/*			displayName: form.nextReceivingFacilityText,*/}
			{/*		}}*/}
			{/*		// className="error"*/}
			{/*		onSelect={(data: DropDownItem) => {*/}
			{/*			saveForm({*/}
			{/*				...form,*/}
			{/*				nextReceivingFacility: data.code,*/}
			{/*				nextReceivingFacilityText: data.displayName,*/}
			{/*			});*/}
			{/*		}}*/}
			{/*		component={FormDropDownListWithSearch}*/}
			{/*	/>*/}
			</div>
		</>
	);
};

export default FormStepOne;

const ComboBoxItem: FC<FieldRenderProps> = ({
	setValueS,
	currentValue,
	onChange,
	...props
}) => {
	const [value, setValue] = useState({
		code: currentValue.code,
		name: currentValue.name,
	});

	useEffect(() => {
		if (currentValue) {
			setValue({
				code: currentValue.code || "",
				name: currentValue.name || "",
			});
		}
	}, [currentValue]);
	const handleOnChange = (event: any) => {
		const ev = event.nativeEvent;
		if (!ev || ev.code === "Enter" || ev.type === "focusout") {
			props.onSelect(value);
			return;
		}
		props.onSelect(event.value);
	};
	const focusedItemIndex = (data: any[], inputText: any, textField: any) => {
		if (inputText !== value.code || inputText !== value.name) {
			setValue({ code: inputText, name: inputText });
		}
		let text = inputText.toLowerCase();
		return data.findIndex((item: any) =>
			String(textField ? item[textField] : item)
				.toLowerCase()
				.includes(text),
		);
	};
	// <ComboBox focusedItemIndex={focusedItemIndex} />;
	return (
		<FieldWrapper style={props.wrapperStyle}>
			<ItemLabel id={props.id} text={props.label} />

			<div>
				<ComboBox
					data={props.data}
					value={value}
					onChange={handleOnChange}
					onBlur={handleOnChange}
					valid={props.isValid}
					allowCustom={true}
					placeholder="Please select ..."
					onOpen={props.loadDataOnClicked}
					textField={props.textField}
					dataItemKey={props.dataItemKey}
					focusedItemIndex={focusedItemIndex}
					// {...props}
				/>
			</div>
		</FieldWrapper>
	);
};

const formatDate = (date: Date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");

	// return `${day}.${month}.${year}`;
	return `${year}-${month}-${day}`;
};

const handleIsValid = (showError: boolean, requirement: boolean) => {
	if (!showError) return !showError;
	return requirement;
};
